import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, AlertTriangle, Archive } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const empty = { name: "", code: "", address: "", active: true };

export default function Branches() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [delTarget, setDelTarget] = useState(null); // { branch, deps }
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const save = async () => {
    try {
      if (editing) await api.put(`/branches/${editing.id}`, form);
      else await api.post("/branches", form);
      toast.success("Saved");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["branches"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDelete = async (b) => {
    try {
      const deps = (await api.get(`/branches/${b.id}/dependencies`)).data;
      setDelTarget({ branch: b, deps });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/branches/${delTarget.branch.id}`);
      toast.success("Branch permanently deleted");
      setDelTarget(null);
      qc.invalidateQueries({ queryKey: ["branches"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDeactivate = async () => {
    try {
      const b = delTarget.branch;
      await api.put(`/branches/${b.id}`, { name: b.name, code: b.code, address: b.address || "", active: false });
      toast.success("Branch deactivated (history preserved)");
      setDelTarget(null);
      qc.invalidateQueries({ queryKey: ["branches"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deps = delTarget?.deps;
  const depRows = deps ? [
    ["Employees", deps.employees], ["Attendance", deps.attendance], ["Payroll", deps.payroll],
    ["Holidays", deps.holidays], ["Leaves", deps.leaves], ["Advances", deps.advances], ["Reimbursements", deps.reimbursements],
  ].filter(([, v]) => v > 0) : [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Branches</h1>
        <Button data-testid="add-branch-button" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(branches || []).map((b) => (
          <div key={b.id} data-testid={`branch-card-${b.code}`} className="bg-white rounded-2xl p-5 border border-slate-200">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-head font-bold text-slate-900">{b.name}</div>
                <div className="text-xs font-mono text-slate-400">{b.code}</div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-branch-${b.code}`} onClick={() => { setEditing(b); setForm(b); setOpen(true); }} className="p-2 text-slate-400 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-branch-${b.code}`} onClick={() => openDelete(b)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-2">{b.address || "—"}</p>
            <span className={`inline-block mt-3 text-xs px-2 py-1 rounded-full ${b.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{b.active ? "Active" : "Inactive"}</span>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Branch" : "New Branch"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Name</label><Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Code</label><Input data-testid="branch-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Address</label><Input data-testid="branch-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
            <Button data-testid="save-branch-button" onClick={save} disabled={!form.name || !form.code} className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-600" /> Delete Branch · {delTarget?.branch?.name}</DialogTitle></DialogHeader>
          {deps?.can_delete ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">This branch has <b>no linked records</b>. This action is permanent and cannot be undone.</p>
              <Button data-testid="confirm-delete-branch" onClick={doDelete} className="w-full bg-red-600 hover:bg-red-700">Permanently Delete</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                This branch has linked records, so it cannot be permanently deleted. Deactivate it instead to keep historical data intact.
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm" data-testid="branch-deps-list">
                {depRows.map(([label, v]) => (
                  <div key={label} className="flex justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                    <span className="text-slate-500">{label}</span><span className="font-semibold text-slate-900">{v}</span>
                  </div>
                ))}
              </div>
              {delTarget?.branch?.active ? (
                <Button data-testid="deactivate-branch" onClick={doDeactivate} className="w-full bg-slate-800 hover:bg-slate-900"><Archive className="h-4 w-4 mr-1" /> Deactivate / Archive Branch</Button>
              ) : (
                <p className="text-sm text-slate-500">This branch is already inactive.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
